import {heicTo} from 'heic-to'
import {FC, useState, useEffect, useRef} from 'react'
import Spinner from '../../../components/spinner/Spinner'
import When from '../../../components/when/When'
import clsx from 'clsx'
import TextWithTooltip from 'components/section/TextWithTooltip'
import {
  detectHeicMimeType,
  getPatientImageHeaders,
  isPatientDriveImageUrl,
} from 'utils/patientImage'

interface Props {
  className?: string
  src: string
  onClick?: any
  alt?: string
  showLoading?: boolean
  fileName?: string
  showFileName?: boolean
  size?: number
  isUnread?: boolean
}

export const Image: FC<Props> = (props) => {
  const {
    className,
    src,
    onClick,
    alt,
    showLoading,
    fileName,
    showFileName = false,
    size = 40,
    isUnread = false,
  } = props
  const [convertedImageUrl, setConvertedImageUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const convertedBlobUrlRef = useRef<string | null>(null)

  const revokeConvertedBlobUrl = () => {
    if (!convertedBlobUrlRef.current) return
    URL.revokeObjectURL(convertedBlobUrlRef.current)
    convertedBlobUrlRef.current = null
  }

  const getPathName = (value: string) => {
    try {
      return decodeURIComponent(new URL(value).pathname).toLowerCase()
    } catch {
      return decodeURIComponent(String(value).split('?')[0] || '').toLowerCase()
    }
  }

  const isHeicSource = (value: string) => {
    const pathName = getPathName(value)
    return pathName.endsWith('.heic') || pathName.endsWith('.heif')
  }

  const getHeicMimeType = (value: string) => {
    const pathName = getPathName(value)
    if (pathName.endsWith('.heif')) return 'image/heif'
    return 'image/heic'
  }

  useEffect(() => {
    let isActive = true
    const controller = new AbortController()
    const cleanup = () => {
      isActive = false
      controller.abort()
      revokeConvertedBlobUrl()
    }
    setLoading(true)
    revokeConvertedBlobUrl()
    setConvertedImageUrl('')

    if (!src) {
      setConvertedImageUrl('')
      setLoading(false)
      return cleanup
    }

    const isPatientImage = isPatientDriveImageUrl(src)
    if (!isPatientImage && !isHeicSource(src)) {
      setConvertedImageUrl(src)
      return cleanup
    }

    const fetchAndConvertImage = async () => {
      try {
        const response = await fetch(src, {
          signal: controller.signal,
          ...(isPatientImage
            ? {credentials: 'include' as const, headers: getPatientImageHeaders()}
            : {}),
        })
        if (!response.ok) {
          throw new Error(`Failed to fetch image. Status: ${response.status}`)
        }

        const sourceBlob = await response.blob()
        if (!isActive) return
        const detectedMimeType = await detectHeicMimeType(sourceBlob)
        if (!isActive) return
        let imageBlob = sourceBlob
        if (detectedMimeType || isHeicSource(src) || /image\/hei[cf]/i.test(sourceBlob.type)) {
          const type =
            detectedMimeType ??
            (/image\/hei[cf]/i.test(sourceBlob.type) ? sourceBlob.type : getHeicMimeType(src))
          const blob = new Blob([sourceBlob], {type})
          const convertedResult = await heicTo({blob, type: 'image/png', quality: 0.9})
          imageBlob = Array.isArray(convertedResult) ? convertedResult[0] : convertedResult
        }

        if (!(imageBlob instanceof Blob)) {
          throw new Error('Invalid converted image blob')
        }

        if (!isActive) return
        const convertedUrl = URL.createObjectURL(imageBlob)
        convertedBlobUrlRef.current = convertedUrl
        setConvertedImageUrl(convertedUrl)
      } catch (error) {
        if (isActive) {
          console.error('Error loading image:', error)
          // Keep direct loading available when fetch is blocked or native HEIC is supported.
          setConvertedImageUrl(src)
        }
      }
    }

    void fetchAndConvertImage()

    return cleanup
  }, [src])

  const handleImageLoad = () => {
    if (convertedImageUrl) setLoading(false)
  }

  const handleImageError = () => {
    if (convertedImageUrl) setLoading(false)
  }

  return (
    <>
      <When isTrue={showLoading && loading}>
        <Spinner {...{className, loading, size: size}} />
      </When>
      <When isTrue={isUnread}>
        <div className='w-2 h-2 bg-red rounded-full absolute left-11 top-4'> </div>
      </When>
      <img
        className={clsx(className, `${showLoading ? `${loading ? 'hidden' : 'block'}` : ''}`)}
        src={convertedImageUrl || undefined}
        onClick={onClick}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      <When isTrue={showFileName}>
        <TextWithTooltip className='cursor-pointer'>{fileName}</TextWithTooltip>
      </When>
    </>
  )
}
